// What the page builder still needs from Orbit's forms module: the theme fields
// a page shares with its form, and the slug helpers. Forms on NL-AOS pages are
// CRM forms (an iframe of /f/<slug>), so Orbit's form engine (fields,
// validation, quizzes, submit config) is gone.

// Mirror PageStyle / PageFont from pages.ts. Duplicated here (not imported)
// because pages.ts imports FormTheme from this file: importing back is circular.
// Structurally identical to PageStyle/PageFont, so a FormTheme.style passes
// straight into styleFor() / fontFor().
export type FormStyle =
  | 'white' | 'linear' | 'glass' | 'corporate' | 'editorial' | 'playful' | 'brutalist'
  | 'light' | 'dark' | 'bold' | 'cream' | 'midnight' | 'ocean' | 'slate' | 'gradient' | 'pastel' | 'aurora' | 'neon'
  | 'clean' | 'letter' | 'navy' | 'paper' | 'mono' | 'program' | 'atelier' | 'impact' | 'sunny';
export type FormFont = 'jakarta' | 'inter' | 'manrope' | 'sora' | 'space' | 'bricolage' | 'figtree' | 'playfair' | 'fraunces' | 'cormorant'
  | 'poppins' | 'oswald' | 'instrument' | 'outfit' | 'lato';

/** The theme fields a page shares with its form: brand, logo, style and font. */
export interface FormTheme {
  brand_color?: string; logo_url?: string; style?: FormStyle; font?: FormFont;
  /** Kept for pages copied from Orbit. CRM forms label their own buttons. */
  submit_label?: string;
}

/** Longest page link, the same cap as a CRM form link (utils/crmForms.js slugify). */
export const MAX_SLUG = 60;

/** A page link from any name: lowercase letters, digits and single dashes, at
 *  most 60 characters, accents dropped. Same rules as the server's CRM slugify,
 *  so /p/<slug> and /f/<slug> behave alike. Pure. */
export function slugify(name: string): string {
  return String(name ?? '').toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG)
    .replace(/-+$/g, '');
}

// High-entropy suffix for public slugs. A timestamp suffix was guessable;
// ~13 chars of crypto-random base36 makes a public slug an unguessable capability.
export function publicToken(): string {
  const b = new Uint8Array(8);
  (globalThis.crypto ?? crypto).getRandomValues(b);
  let n = 0n;
  for (const x of b) n = (n << 8n) | BigInt(x);
  return n.toString(36).padStart(13, '0').slice(0, 13);
}

/** A well-formed page link: lowercase words joined by single dashes, at most 60 characters. */
export function isValidSlug(slug: string): boolean {
  return slug.length <= MAX_SLUG && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
}
